import type { JsonSchemaType } from '@gmn-portal/backend';
import { useQueryClient } from '@tanstack/react-query';
import { Save, Sliders } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useGlobalMetadata, useUpdateGlobalMetadata, useUserGroups } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { PageContainer, PageTitle, SimpleBreadcrumbItem } from '@/shared/components/layout';
import {
  type FieldError,
  parseMetadataApiErrors,
} from '@/shared/components/metadata/metadata-builder';
import { MetadataBuilderWithPreview } from '@/shared/components/metadata/metadata-builder/metadata-builder-with-preview';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui';
import { useUnsavedChangesGuard } from '@/shared/hooks/use-unsaved-changes-guard';
import { isGmnAdminGroup } from '@/shared/utils/permissions';

// Default empty schema
const defaultMetadataSchema: JsonSchemaType = {
  type: 'object',
  groups: [],
  properties: {},
  required: [],
  propertyOrder: [],
};

export function ManageContractMetadata() {
  const { t } = useIntl();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const { data: globalMetadata, isLoading } = useGlobalMetadata();
  const { data: userGroupsData } = useUserGroups({ limit: 0, sort: 'key' });
  const userGroups = userGroupsData?.docs ?? [];
  const updateGlobalMetadata = useUpdateGlobalMetadata();
  const [isUpdating, setIsUpdating] = useState(false);
  const [metadataSchema, setMetadataSchema] = useState<JsonSchemaType>(defaultMetadataSchema);
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);
  const initialSchemaRef = useRef<JsonSchemaType>(defaultMetadataSchema);

  const isGmnAdmin = isGmnAdminGroup(currentUser);

  // Load existing metadata when data is available
  useEffect(() => {
    if (globalMetadata && !isLoading) {
      const nextSchema =
        (globalMetadata.rootContractMetadata as JsonSchemaType) || defaultMetadataSchema;
      setMetadataSchema(nextSchema);
      initialSchemaRef.current = nextSchema;
    }
  }, [globalMetadata, isLoading]);

  const handleSubmit = async () => {
    if (!isGmnAdmin) return;

    setIsUpdating(true);
    setFieldErrors([]); // Clear previous errors

    try {
      await updateGlobalMetadata.mutateAsync({
        rootContractMetadata: metadataSchema,
      });

      await queryClient.invalidateQueries({ queryKey: ['globalMetadata'] });
      toast.success(t('metadata.contractMetadataUpdatedSuccessfully'));
    } catch (error) {
      // Parse field-specific errors
      const parsedErrors = parseMetadataApiErrors(error, (key) => t(key));
      setFieldErrors(parsedErrors);

      // Handle remaining errors with toasts
      const payloadError = error as {
        response?: {
          errors?: Array<{
            data?: {
              errors?: Array<{
                code: string;
                message: string;
              }>;
            };
            message?: string;
          }>;
        };
      };

      if (payloadError.response?.errors) {
        payloadError.response.errors.forEach((err) => {
          if (err.data?.errors) {
            err.data.errors.forEach((apiError) => {
              // Only show toast for errors that weren't mapped to fields
              if (
                ![
                  'NewRequiredFieldAdditionNotAllowed',
                  'ExistingFieldMadeRequiredWithMissingValues',
                  'FieldTypeChangeNotAllowed',
                ].includes(apiError.code)
              ) {
                toast.error(t('metadata.metadataUpdateFailed'), {
                  description: apiError.message,
                });
              }
            });
          } else if (err.message) {
            toast.error(t('errors.unknownError'), {
              description: err.message,
            });
          }
        });
      } else if (parsedErrors.length === 0) {
        toast.error(t('errors.unknownError'));
      }
    }

    setIsUpdating(false);
  };

  // Clear errors when schema changes
  const handleSchemaChange = (schema: JsonSchemaType) => {
    setMetadataSchema(schema);
    if (fieldErrors.length > 0) {
      setFieldErrors([]);
    }
  };

  const isSchemaDirty = useMemo(
    () => JSON.stringify(metadataSchema) !== JSON.stringify(initialSchemaRef.current),
    [metadataSchema]
  );

  const discardChanges = () => {
    setMetadataSchema(initialSchemaRef.current);
    setFieldErrors([]);
  };

  const { dialog: unsavedDialog } = useUnsavedChangesGuard({
    when: isSchemaDirty,
    onDiscard: discardChanges,
  });

  return (
    <PageContainer>
      <PageTitle
        icon={Sliders}
        breadcrumb={
          <>
            <SimpleBreadcrumbItem>{t('navigation.items.gmnSettings')}</SimpleBreadcrumbItem>
            <SimpleBreadcrumbItem isLast>{t('metadata.contractMetadata')}</SimpleBreadcrumbItem>
          </>
        }
        title={t('metadata.contractMetadata')}
        primaryAction={{
          label: isUpdating ? t('common.saving') : t('common.save'),
          onClick: handleSubmit,
          icon: <Save className="size-4" />,
          disabled: !isGmnAdmin || isUpdating,
        }}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('metadata.rootContractMetadata')}</CardTitle>
          <CardDescription>{t('metadata.rootContractMetadataDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="min-h-[500px]">
            <MetadataBuilderWithPreview
              value={metadataSchema}
              onChange={handleSchemaChange}
              disabled={!isGmnAdmin || isLoading}
              fieldErrors={fieldErrors}
              userGroups={userGroups}
              header={false}
              storageKey="gmn.metadataBuilder.contractMetadata.formPreview.open"
            />
          </div>
        </CardContent>
      </Card>

      {unsavedDialog}
    </PageContainer>
  );
}
